import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Interactive } from "../src/interactions/Interactive";
import { useInteractionStore } from "../src/stores/useInteraction";

const STANDBY_INDEX = 2;
const PROXIMITY_INDEX = 0;
const INTERACTION_INDEX = 1;
const PROXIMITY_RADIUS = 10;
const ROTATION_DURATION = 0.2;

export default function Orbsman({
	position = [0, 0, 0],
	rotation = [0, 0, 0],
	scale = 1,
	targetRef = null,
	...props
}) {
	const group = useRef();
	const { scene, animations } = useGLTF("/interactive/orbsman.glb");
	const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
	const { actions } = useAnimations(animations, group);

	const actionNames = useMemo(() => Object.keys(actions || {}), [actions]);
	const getActionName = useCallback(
		(index) =>
			actionNames.length > 0
				? actionNames[Math.min(index, actionNames.length - 1)]
				: undefined,
		[actionNames]
	);
	const standbyName = getActionName(STANDBY_INDEX);
	const proximityName = getActionName(PROXIMITY_INDEX);
	const interactionName = getActionName(INTERACTION_INDEX);

	const baseEuler = useMemo(
		() => new THREE.Euler(rotation[0], rotation[1], rotation[2]),
		[rotation]
	);
	const orbsWorldPos = useMemo(() => new THREE.Vector3(), []);
	const targetWorldPos = useMemo(() => new THREE.Vector3(), []);
	const lookDirection = useMemo(() => new THREE.Vector3(), []);
	const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);
	const modelBounds = useMemo(() => new THREE.Box3(), []);
	const modelCenter = useMemo(() => new THREE.Vector3(), []);

	const cameraPosition = useMemo(() => {
		const offset = { x: 0, y: 4.5, z: 10 };
		const yaw = rotation?.[1] ?? 0;
		const cosY = Math.cos(yaw);
		const sinY = Math.sin(yaw);
		const offsetX = offset.x * cosY - offset.z * sinY;
		const offsetZ = offset.x * sinY + offset.z * cosY;
		return [
			(position?.[0] ?? 0) + offsetX,
			(position?.[1] ?? 0) + offset.y,
			(position?.[2] ?? 0) + offsetZ,
		];
	}, [position, rotation]);

	const isPlayingRef = useRef(false);
	const proximityTriggeredRef = useRef(false);
	const playStandbyRef = useRef(null);
	const triggerProximityRef = useRef(null);
	const playInteractionRef = useRef(null);
	const proximityActionRef = useRef(null);
	const currentModeRef = useRef("standby");
	const standbyActionRef = useRef(null);
	const interactionActionRef = useRef(null);
	const playerHiddenRef = useRef(false);

	const camera = useThree((state) => state.camera);
	const playerHidden = useInteractionStore((state) => state.playerHidden);

	const lerpAngle = useCallback((current, target, alpha) => {
		let diff = ((target - current + Math.PI) % (Math.PI * 2)) - Math.PI;
		if (diff < -Math.PI) diff += Math.PI * 2;
		return current + diff * alpha;
	}, []);

	const TextOptions = useMemo(
		() => [
			"#@$^^ %*##&^^ *&#$&&@ $%^&*# $@#$#$%^& &%$#@!&*?#$\n(¿Haz visto qué están haciendo en el escenario del fondo?)",
			"#?*&$@& %$#! @#&*?!*&% #@#$%^* #?*&$@&\n(Consejo: si te tiras desde aquí te darán una vida extra!)",
			"##%^^&@##@$^^ %*##&^^ *&#$&&@ $%^&*# $@#$#$%^&\n(¿Cuando aparecieron todas estas monedas por aquí?)",
			"&%$#@!&*?#$ @#&*?!*&% #@#$%^* #?*&$@& %$#! @#&*?!*&%\n(Esa rata deforme de abajo me provoca incomodidad...)",
			"@#&*?!*&% #@#$%^* #?*&$@& %$#! @#&*?!*&% $@!#&*?\n(La carpa del circo huele a palomitas de maiz)",
		],
		[]
	);

	const hudIndexRef = useRef(0);

	const getNextHudText = useCallback(() => {
		if (TextOptions.length === 0) return "";
		const message = TextOptions[hudIndexRef.current];
		hudIndexRef.current = (hudIndexRef.current + 1) % TextOptions.length;
		return message;
	}, [TextOptions]);

	const interactive = useMemo(
		() =>
			new Interactive({
				id: "orbsman",
				promptText: "Interactuar [E]",
				hudText: getNextHudText,
				radius: PROXIMITY_RADIUS,
				cameraConfig: {
					position: [cameraPosition[0], cameraPosition[1], cameraPosition[2]],
					transitionDuration: 1.1,
				},
				getCameraTarget: () => {
					if (!group.current) return null;
					modelBounds.setFromObject(group.current);
					modelBounds.getCenter(modelCenter);
					return [modelCenter.x, modelCenter.y, modelCenter.z];
				},
			}),
		[cameraPosition, modelBounds, modelCenter, getNextHudText]
	);

	useEffect(() => () => interactive.dispose(), [interactive]);

	useEffect(() => {
		clonedScene.traverse((child) => {
			if (child.isMesh || child.isSkinnedMesh) {
				child.castShadow = true;
				child.receiveShadow = true;
			}
		});
	}, [clonedScene]);

	useEffect(() => {
		if (group.current) {
			group.current.rotation.set(baseEuler.x, baseEuler.y, baseEuler.z);
		}
	}, [baseEuler]);

	useEffect(() => {
		playerHiddenRef.current = playerHidden;

		if (playerHidden) {
			if (currentModeRef.current !== "interaction") {
				playInteractionRef.current && playInteractionRef.current();
			}
		} else if (currentModeRef.current !== "proximity") {
			playStandbyRef.current && playStandbyRef.current();
		}
	}, [playerHidden]);

	useEffect(() => {
		if (!actions || !standbyName) return undefined;

		const standbyAction = actions[standbyName];
		if (!standbyAction) return undefined;

		const proximityAction = proximityName ? actions?.[proximityName] : null;
		const interactionAction = interactionName ? actions?.[interactionName] : null;
		const allActions = Object.values(actions);

		const stopAllExcept = (keep) => {
			allActions.forEach((clip) => {
				if (clip && clip !== keep) {
					clip.stop();
				}
			});
		};

		const playStandby = () => {
			stopAllExcept(standbyAction);
			standbyAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.15).play();
			standbyAction.clampWhenFinished = false;
			isPlayingRef.current = false;
			currentModeRef.current = "standby";
		};

		const playProximity = () => {
			if (!proximityAction) return;
			isPlayingRef.current = true;
			currentModeRef.current = "proximity";
			interactionAction?.fadeOut(0.1);
			interactionAction?.stop();
			standbyAction.fadeOut(0.1);
			proximityAction
				.reset()
				.setLoop(THREE.LoopOnce, 1)
				.fadeIn(0.1)
				.play();
			proximityAction.clampWhenFinished = true;
		};

		const playInteraction = () => {
			if (!interactionAction) return;
			stopAllExcept(interactionAction);
			interactionAction
				.reset()
				.setLoop(THREE.LoopRepeat, Infinity)
				.fadeIn(0.15)
				.play();
			interactionAction.clampWhenFinished = false;
			isPlayingRef.current = false;
			currentModeRef.current = "interaction";
		};

		playStandbyRef.current = playStandby;
		triggerProximityRef.current = proximityAction ? playProximity : null;
		playInteractionRef.current = interactionAction ? playInteraction : null;
		proximityActionRef.current = proximityAction ?? null;
		standbyActionRef.current = standbyAction;
		interactionActionRef.current = interactionAction ?? null;

		playStandby();

		const mixer =
			proximityAction?._mixer || interactionAction?._mixer || standbyAction?._mixer || null;

		const handleFinished = (event) => {
			if (!event?.action) return;
			if (event.action === proximityAction) {
				isPlayingRef.current = false;
				if (playerHiddenRef.current) {
					playInteraction();
				} else {
					playStandby();
				}
			}
		};

		if (mixer) {
			mixer.addEventListener("finished", handleFinished);
		}

		return () => {
			if (mixer) {
				mixer.removeEventListener("finished", handleFinished);
			}
			standbyAction.stop();
			proximityAction?.stop();
			interactionAction?.stop();
			isPlayingRef.current = false;
			currentModeRef.current = "standby";
			playStandbyRef.current = null;
			triggerProximityRef.current = null;
			playInteractionRef.current = null;
			proximityActionRef.current = null;
			standbyActionRef.current = null;
			interactionActionRef.current = null;
		};
	}, [actions, standbyName, proximityName, interactionName]);

	useFrame((_, delta) => {
		const orbsGroup = group.current;
		if (!orbsGroup) return;

		orbsGroup.rotation.x = baseEuler.x;
		orbsGroup.rotation.z = baseEuler.z;

		const targetGroup = targetRef?.current?.group;
		if (!targetGroup || typeof targetGroup.translation !== "function") {
			interactive.setWithinRadius(false);
			const alpha = Math.min(1, delta / ROTATION_DURATION);
			orbsGroup.rotation.y = lerpAngle(orbsGroup.rotation.y, baseEuler.y, alpha);
			return;
		}

		const translation = targetGroup.translation();
		if (!translation) {
			interactive.setWithinRadius(false);
			const alpha = Math.min(1, delta / ROTATION_DURATION);
			orbsGroup.rotation.y = lerpAngle(orbsGroup.rotation.y, baseEuler.y, alpha);
			return;
		}

		targetWorldPos.set(translation.x, translation.y, translation.z);
		orbsGroup.getWorldPosition(orbsWorldPos);

		const distance = orbsWorldPos.distanceTo(targetWorldPos);
		interactive.setWithinRadius(distance <= interactive.radius, distance);

		let desiredYaw = baseEuler.y;

		if (distance > PROXIMITY_RADIUS) {
			proximityTriggeredRef.current = false;

			if (!playerHidden && currentModeRef.current !== "standby") {
				playStandbyRef.current && playStandbyRef.current();
			}

			const alpha = Math.min(1, delta / ROTATION_DURATION);
			orbsGroup.rotation.y = lerpAngle(orbsGroup.rotation.y, desiredYaw, alpha);
			return;
		}

		lookDirection.subVectors(targetWorldPos, orbsWorldPos);
		lookDirection.y = 0;

		if (playerHidden) {
			camera.getWorldPosition(cameraWorldPos);
			lookDirection.subVectors(cameraWorldPos, orbsWorldPos);
			lookDirection.y = 0;
		}

		if (lookDirection.lengthSq() > 1e-5) {
			lookDirection.normalize();
			desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
		}

		if (!playerHidden && !proximityTriggeredRef.current && !isPlayingRef.current) {
			proximityTriggeredRef.current = true;
			triggerProximityRef.current && triggerProximityRef.current();
		}

		if (playerHidden && currentModeRef.current !== "interaction") {
			playInteractionRef.current && playInteractionRef.current();
		}

		if (
			currentModeRef.current === "interaction" &&
			interactionActionRef.current &&
			typeof interactionActionRef.current.isRunning === "function" &&
			!interactionActionRef.current.isRunning()
		) {
			playInteractionRef.current && playInteractionRef.current();
		}

		if (
			currentModeRef.current === "standby" &&
			proximityActionRef.current &&
			typeof proximityActionRef.current.isRunning === "function" &&
			!proximityActionRef.current.isRunning()
		) {
			isPlayingRef.current = false;
		}

		const alpha = Math.min(1, delta / ROTATION_DURATION);
		orbsGroup.rotation.y = lerpAngle(orbsGroup.rotation.y, desiredYaw, alpha);
	});

	return (
		<primitive
			ref={group}
			object={clonedScene}
			position={position}
			scale={scale}
			{...props}
		/>
	);
}

useGLTF.preload("/interactive/orbsman.glb");
